/**
 * ============================================================================
 *   Copyright (c) 2026 Dobler Data Solutions, LLC. ALL RIGHTS RESERVED.
 * ============================================================================
 *   Module       : DetailLayout
 *   Feature      : 197-portal-light-theme-responsive (design blueprint)
 *   Description  : Canonical detail-page shell — a back affordance, then a title
 *                  (with optional icon + status badges) and right-aligned
 *                  actions, then optional tabs, then content. Standardizes the
 *                  varied, often-raw detail pages onto one structure.
 *   Blueprint    : docs/design-blueprint.md
 *   Constitution : v1.19.0
 *   Author       : Peter Dobler
 * ============================================================================
 */
import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { clsx } from 'clsx';
import { Tabs } from './Tabs';
import type { TabItem } from './Tabs';
import { RefreshButton } from './RefreshButton';
import { PageHelpButton } from './PageHelpButton';

export interface DetailLayoutProps {
  title: React.ReactNode;
  subtitle?: React.ReactNode;
  icon?: React.ReactNode;
  /** Status chips rendered next to the title. */
  badges?: React.ReactNode;
  actions?: React.ReactNode;
  /** Route to return to (renders a "Back" link above the title). */
  backTo?: string;
  backLabel?: string;
  /** Optional tab bar under the header (controlled). */
  tabs?: TabItem[];
  activeTab?: string;
  onTabChange?: (key: string) => void;
  children: React.ReactNode;
  className?: string;

  // ── Spec 226 — Universal Page Affordances (optional) ──
  helpId?: string;
  onRefresh?: () => void | Promise<void>;
  isRefreshing?: boolean;
  lastFetchedAt?: Date | number | string | null;
  collectedAt?: Date | number | string | null;
  refreshError?: unknown;
}

/** Canonical detail-page shell. */
export const DetailLayout: React.FC<DetailLayoutProps> = ({
  title, subtitle, icon, badges, actions, backTo, backLabel = 'Back', tabs, activeTab, onTabChange, children, className,
  helpId, onRefresh, isRefreshing, lastFetchedAt, collectedAt, refreshError,
}) => (
  <div className="p-4 sm:p-6">
    <div className={clsx('w-full', className)}>
      {backTo && (
        <Link
          to={backTo}
          className="mb-3 inline-flex items-center gap-1 text-[length:var(--text-sm)] text-[var(--color-text-secondary)] transition-colors hover:text-[var(--color-accent)]"
        >
          <ArrowLeft className="h-4 w-4" /> {backLabel}
        </Link>
      )}
      <div className="mb-[var(--space-section)] flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="flex min-w-0 items-start gap-3">
          {icon && <div className="mt-0.5 flex-shrink-0 text-[var(--color-accent)]">{icon}</div>}
          <div className="min-w-0">
            <h1 className="flex flex-wrap items-center gap-2 text-[length:var(--text-h1)] font-semibold leading-[var(--leading-h1)] text-[var(--color-text-primary)]">
              <span className="truncate">{title}</span>
              {badges}
            </h1>
            {subtitle && <p className="mt-1 text-[length:var(--text-sm)] text-[var(--color-text-secondary)]">{subtitle}</p>}
          </div>
        </div>
        {(onRefresh || helpId || actions) && (
          <div className="flex flex-shrink-0 flex-wrap items-center gap-2">
            {onRefresh && (
              <RefreshButton
                onRefresh={onRefresh}
                isRefreshing={isRefreshing}
                lastFetchedAt={lastFetchedAt}
                collectedAt={collectedAt}
                error={refreshError}
              />
            )}
            {helpId && <PageHelpButton helpId={helpId} />}
            {actions}
          </div>
        )}
      </div>
      {tabs && activeTab !== undefined && onTabChange && (
        <Tabs tabs={tabs} value={activeTab} onChange={onTabChange} className="mb-[var(--space-section)]" />
      )}
      {children}
    </div>
  </div>
);

export default DetailLayout;
